import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LegalPageLayout from './LegalPageLayout';
import { useAuthStore } from '../../store/authStore';
import { authApi } from '../../services/endpoints';
import { SUPPORT_EMAIL } from '../../config';

/**
 * E1 — real, public account-deletion page.
 *
 * Calls the SAME backend endpoint (DELETE /auth/account/v2) the mobile
 * app's in-app deletion and the customer portal's "Danger Zone" (added
 * in the prior G2 phase) already use — never a fake client-only flow, per
 * the explicit instruction. Reachable without authentication (Google
 * Play's own requirement), but the actual deletion action requires a
 * real, logged-in session, since deleting an account you're not
 * authenticated as isn't something this page can or should allow.
 * Visitors who aren't logged in get the always-valid alternative: a
 * documented email request path.
 */
export default function AccountDeletion() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const initialized = useAuthStore((s) => s.initialized);
  const initialize = useAuthStore((s) => s.initialize);
  const logout = useAuthStore((s) => s.logout);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const emailMatches = emailInput.trim().toLowerCase() === (user?.email ?? '').toLowerCase();

  const handleDelete = async () => {
    setError('');
    setDeleting(true);
    try {
      await authApi.deleteAccount();
      await logout();
      navigate('/');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to delete account. Please try again or contact support.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <LegalPageLayout title="Account Deletion">
      <p>
        You can permanently delete your Sheba Smart Home account and all
        associated data — homes, devices, cameras, recordings, scenes, and
        automations — at any time. This action cannot be undone.
      </p>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">What gets deleted</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>Your account profile (name, email, password).</li>
          <li>Homes, rooms, devices, cameras, scenes, and automations you own.</li>
          <li>Recordings, event history, and notification preferences.</li>
        </ul>
        <p>
          Some records (for example, security or transaction logs) may be
          retained for a limited period where required for legal or fraud-
          prevention purposes — see our{' '}
          <a href="/privacy-policy" className="text-[#00d4ff]">Privacy Policy</a> §5.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">Delete your account</h2>

        {!initialized && (
          <p className="text-white/50">Checking your session…</p>
        )}

        {initialized && user && (
          <div className="mt-4 space-y-3 border border-red-500/30 bg-red-500/5 rounded-lg p-5 not-prose">
            <p className="text-sm text-white/70">
              Signed in as <strong>{user.email}</strong>. Type your email address
              below to confirm permanent deletion.
            </p>
            {!confirmOpen ? (
              <button
                onClick={() => setConfirmOpen(true)}
                className="btn-secondary !bg-red-600 hover:!bg-red-500 !text-white"
              >
                Delete my account
              </button>
            ) : (
              <div className="space-y-3">
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="Enter your email to confirm"
                  className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/20 text-white focus:outline-none focus:border-[#00d4ff]"
                  autoComplete="off"
                />
                {error && <p className="text-red-400 text-sm">{error}</p>}
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleDelete}
                    disabled={!emailMatches || deleting}
                    className="btn-secondary !bg-red-600 hover:!bg-red-500 !text-white disabled:opacity-50"
                  >
                    {deleting ? 'Deleting…' : 'Permanently delete my account'}
                  </button>
                  <button
                    onClick={() => { setConfirmOpen(false); setEmailInput(''); setError(''); }}
                    className="text-white/60 hover:text-white text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {initialized && !user && (
          <div className="mt-4 space-y-3 not-prose">
            <p>
              To delete your account directly on this page, please log in first,
              then return to this page.
            </p>
            <p>
              You can also request deletion without logging in by emailing{' '}
              <a href={`mailto:${SUPPORT_EMAIL}?subject=Account%20deletion%20request`} className="text-[#00d4ff]">
                {SUPPORT_EMAIL}
              </a>{' '}
              from the email address associated with your account. We will
              verify your identity and confirm once your account and data have
              been deleted.
            </p>
          </div>
        )}
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">Deleting from the mobile app</h2>
        <p>
          You can also delete your account directly in the mobile app:{' '}
          <strong>Settings → Account → Delete Account</strong>.
        </p>
      </section>
    </LegalPageLayout>
  );
}
